import { useEffect, useState, useCallback } from 'react';
import { tasksApi, listsApi, ApiError } from '../api/client';
import type { TaskRow } from '../api/types';
import type { View } from '../lib/views';

interface UseTasksOpts {
  view: View;
}

export function useTasks({ view }: UseTasksOpts) {
  const [tasks, setTasks] = useState<TaskRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      let data: TaskRow[];
      if (view.kind === 'list') {
        data = await tasksApi.list(view.listId);
        // list view: 不显示已完成
        data = data.filter((t) => !t.completed);
      } else if (view.kind === 'my-day') {
        data = await tasksApi.listMyDay();
      } else if (view.kind === 'important') {
        data = await tasksApi.listStarred();
        data = data.filter((t) => !t.completed);
      } else if (view.kind === 'planned') {
        // V1 暂未实现独立 planned 视图, 用 all + due_date
        data = [];
      } else if (view.kind === 'completed') {
        // 已完成视图: 显示所有 list 的已完成 task
        const allLists = await listsApi.list();
        const results = await Promise.all(
          allLists.map((l) =>
            tasksApi.list(l.id, { onlyCompleted: true }).then((ts) => ts)
          )
        );
        data = results.flat();
      } else if (view.kind === 'archived') {
        // 归档视图: 显示所有已归档的 task
        data = await tasksApi.listArchived();
      } else {
        // all: 拉所有未完成
        data = await tasksApi.listAll();
      }
      setTasks(data);
      setError(null);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [view]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // 监听同步事件 → 自动 refresh
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent).detail as { entity?: string };
      if (!detail?.entity || detail.entity === 'task' || detail.entity === 'step') {
        refresh();
      }
    };
    const onSnapshot = () => refresh();
    window.addEventListener('todo:change', onChange);
    window.addEventListener('todo:snapshot', onSnapshot);
    return () => {
      window.removeEventListener('todo:change', onChange);
      window.removeEventListener('todo:snapshot', onSnapshot);
    };
  }, [refresh]);

  const create = useCallback(
    async (input: Parameters<typeof tasksApi.create>[0]) => {
      const row = await tasksApi.create(input);
      await refresh();
      return row;
    },
    [refresh]
  );

  const update = useCallback(
    async (id: string, patch: Record<string, unknown>) => {
      const row = await tasksApi.update(id, patch);
      // 立即更新本地 tasks 数组里对应 task 的 version + 其他字段
      // 避免快速连点时 task.version 是旧值导致 409
      setTasks((prev) =>
        prev.map((t) => (t.id === id ? { ...t, ...row } : t))
      );
      // 后台 refresh 拉最新 (其他设备的更新)
      refresh();
      return row;
    },
    [refresh]
  );

  const remove = useCallback(
    async (id: string) => {
      await tasksApi.remove(id);
      await refresh();
    },
    [refresh]
  );

  const toggleComplete = useCallback(
    async (task: TaskRow) => {
      return update(task.id, {
        completed: task.completed ? 0 : 1,
        version: task.version,
      });
    },
    [update]
  );

  return { tasks, loading, error, refresh, create, update, remove, toggleComplete };
}